import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DemoRequestDialog({
  open,
  onOpenChange,
}: DemoRequestDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    businessType: "",
    serviceStatus: "",
    message: "",
  });

  // Add this to debug the dialog state

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.fullName ||
      !formData.email ||
      !formData.businessType ||
      !formData.serviceStatus
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      // Call edge function to send email
      const { error } = await supabase.functions.invoke("send-demo-request", {
        body: {
          fullName: formData.fullName,
          email: formData.email,
          businessType: formData.businessType,
          serviceStatus: formData.serviceStatus,
          message: formData.message,
        },
      });

      if (error) {
        console.error("Error sending demo request:", error);
        toast.error("Failed to send demo request. Please try again.");
        return;
      }

      toast.success("Demo request sent successfully! We'll contact you soon.");
      setFormData({
        fullName: "",
        email: "",
        businessType: "",
        serviceStatus: "",
        message: "",
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to send demo request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {t("demo.title")}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {t("demo.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">{t("demo.fullName")} *</Label>
            <Input
              id="fullName"
              type="text"
              placeholder={t("demo.fullNamePlaceholder")}
              value={formData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("demo.email")} *</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("demo.emailPlaceholder")}
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="businessType">{t("demo.businessType")} *</Label>
            <Select
              value={formData.businessType}
              onValueChange={(value) =>
                handleInputChange("businessType", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("demo.businessTypePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="restaurant">
                  {t("demo.businessTypes.restaurant")}
                </SelectItem>
                <SelectItem value="cafe">
                  {t("demo.businessTypes.cafe")}
                </SelectItem>
                <SelectItem value="bar">
                  {t("demo.businessTypes.bar")}
                </SelectItem>
                <SelectItem value="food-truck">
                  {t("demo.businessTypes.foodTruck")}
                </SelectItem>
                <SelectItem value="bakery">
                  {t("demo.businessTypes.bakery")}
                </SelectItem>
                <SelectItem value="fast-food">
                  {t("demo.businessTypes.fastFood")}
                </SelectItem>
                <SelectItem value="fine-dining">
                  {t("demo.businessTypes.fineDining")}
                </SelectItem>
                <SelectItem value="catering">
                  {t("demo.businessTypes.catering")}
                </SelectItem>
                <SelectItem value="other">
                  {t("demo.businessTypes.other")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serviceStatus">{t("demo.serviceStatus")} *</Label>
            <Select
              value={formData.serviceStatus}
              onValueChange={(value) =>
                handleInputChange("serviceStatus", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("demo.serviceStatusPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">
                  {t("demo.serviceStatuses.new")}
                </SelectItem>
                <SelectItem value="replace">
                  {t("demo.serviceStatuses.replace")}
                </SelectItem>
                <SelectItem value="upgrade">
                  {t("demo.serviceStatuses.upgrade")}
                </SelectItem>
                <SelectItem value="evaluate">
                  {t("demo.serviceStatuses.evaluate")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t("demo.message")}</Label>
            <Textarea
              id="message"
              placeholder={t("demo.messagePlaceholder")}
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t("demo.cancel")}
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? t("demo.sending") : t("demo.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
